import React from "react";
import { SearchIcon, ChevronRightIcon, CashIcon, CheckCircleIcon, ClockIcon } from "@heroicons/react/solid";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui";
import StatusCell from "../../components/common/StatusCell";
import BreadCrumbs from "../../components/ui/BreadCrumbs";
import { useAdminPettyCashList } from "../../hooks/petty_cash/useAdminPettyCashList";
import RoundedContainer from "../../components/containers/RoundedContainer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/table/Table";
import InlineLoader from "../../components/loader/InlineLoader";
import Pagination from "../../components/pagination";
import Cookies from "universal-cookie";
import { ROLE, NAME } from "../../config";

const cookies = new Cookies();

function AdminPettyCashList() {
  const navigate = useNavigate();

  const role = cookies.get(ROLE);
  const adminName = cookies.get(NAME);

  const {
    employeeListing,
    statisticsListing,
    empFilters,
    searchQuery,
    handleEmpSearch,
    handlePageClick,
  } = useAdminPettyCashList();

  const stats = statisticsListing.data?.data?.statistics || statisticsListing.data?.data || {};
  const totalIncome = parseFloat(stats.total_income || 0);
  const totalExpenses = parseFloat(stats.total_approved_expenses || 0);
  const totalPending = parseFloat(stats.total_pending_expenses || 0);

  return (
    <div className="py-6 px-4 md:px-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <BreadCrumbs
          crumbs={[
            { label: "Dashboard", link: "/" },
            { label: "Petty Cash", link: "" },
          ]}
        />
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Petty Cash Overview</h1>
            <p className="text-gray-500 mt-1">
              {adminName ? (
                <>
                  Welcome back, <span className="font-bold text-gray-800">{adminName}</span>. Manage employee balances, track disbursements, and approve expense requests.
                </>
              ) : (
                "Manage employee balances, track disbursements, and approve expense requests."
              )}
            </p>
          </div>
        </div>

        {/* Statistics KPI Cards */}
        {role === "COMPANY_ADMIN" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="bg-white border border-gray-155 rounded-2xl p-5 shadow-sm flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Total Income</span>
                {statisticsListing.isLoading ? (
                  <div className="h-8 w-24 bg-gray-200 animate-pulse rounded-lg" />
                ) : (
                  <div className="text-2xl font-bold text-gray-900">&#8377;{totalIncome}</div>
                )}
              </div>
              <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
                <CashIcon className="w-6 h-6" />
              </div>
            </div>

            <div className="bg-white border border-gray-155 rounded-2xl p-5 shadow-sm flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Approved Expenses</span>
                {statisticsListing.isLoading ? (
                  <div className="h-8 w-24 bg-gray-200 animate-pulse rounded-lg" />
                ) : (
                  <div className="text-2xl font-bold text-green-600">&#8377;{totalExpenses}</div>
                )}
              </div>
              <div className="p-3 rounded-xl bg-green-50 text-green-600">
                <CheckCircleIcon className="w-6 h-6" />
              </div>
            </div>

            <div className="bg-white border border-gray-155 rounded-2xl p-5 shadow-sm flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Pending Approvals</span>
                {statisticsListing.isLoading ? (
                  <div className="h-8 w-24 bg-gray-200 animate-pulse rounded-lg" />
                ) : (
                  <div className="text-2xl font-bold text-amber-600">&#8377;{totalPending}</div>
                )}
              </div>
              <div className="p-3 rounded-xl bg-amber-50 text-amber-600">
                <ClockIcon className="w-6 h-6" />
              </div>
            </div>
          </div>
        )}

        {/* Filter Bar */}
        <RoundedContainer className="p-5 shadow-sm border border-gray-155 mt-0">
          <div className="relative w-full md:w-80">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
              <SearchIcon className="h-5 w-5 text-gray-400" />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={handleEmpSearch}
              placeholder="Search by name or email..."
              className="pl-10 pr-4 py-2 w-full rounded-xl border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm shadow-sm transition-all"
            />
          </div>
        </RoundedContainer>

        {employeeListing.isLoading ? (
          <div className="flex items-center justify-center py-12 w-full">
            <InlineLoader className="w-8 h-8 text-primary" />
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <RoundedContainer className="hidden md:block border border-gray-155 shadow-sm p-4">
              <div className="max-w-full overflow-x-auto">
                <Table className="table-auto w-full">
                  <TableHead>
                    <TableHeaderCell>Employee</TableHeaderCell>
                    <TableHeaderCell>Total Income</TableHeaderCell>
                    <TableHeaderCell>Total Expenses</TableHeaderCell>
                    <TableHeaderCell>Pending</TableHeaderCell>
                    <TableHeaderCell className="text-center">Auto-Approve</TableHeaderCell>
                    <TableHeaderCell className="text-center">Actions</TableHeaderCell>
                  </TableHead>
                  <TableBody>
                    {employeeListing.data?.data?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-gray-400">
                          No employees found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      employeeListing.data?.data?.map((emp) => (
                        <TableRow key={emp.company_employee_id} onClick={() => navigate(`/petty-cash/${emp.company_employee_id}`)}>
                          <TableCell>
                            <div>
                              <span className="font-semibold text-gray-800 block">{emp.employee_name}</span>
                              <span className="text-xs text-gray-400">{emp.email}</span>
                            </div>
                          </TableCell>
                          <TableCell className="font-semibold text-green-600">&#8377;{emp.total_income}</TableCell>
                          <TableCell className="font-semibold text-red-600">&#8377;{emp.total_expenses}</TableCell>
                          <TableCell className="text-amber-600 font-medium">&#8377;{emp.total_pending_expenses}</TableCell>
                          <TableCell className="text-center flex justify-center" onClick={(e) => e.stopPropagation()}>
                            <StatusCell
                              id={emp.company_employee_id}
                              idKey="company_employee_id"
                              initialStatus={emp.auto_approval_petty_cash || false}
                              url="petty-cash/update_auto_approval_status"
                              refreshUrl={["petty-cash/employee_list", "petty-cash/statistics"]}
                            />
                          </TableCell>
                          <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/petty-cash/${emp.company_employee_id}`)}
                              className="inline-flex items-center space-x-1"
                            >
                              <span>View Ledger</span>
                              <ChevronRightIcon className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </RoundedContainer>

            {/* Mobile Card List View */}
            <div className="md:hidden space-y-4">
              {employeeListing.data?.data?.length === 0 ? (
                <div className="text-center py-8 text-gray-400 bg-white rounded-2xl border border-gray-150">
                  No employees found.
                </div>
              ) : (
                employeeListing.data?.data?.map((emp) => (
                  <div
                    key={emp.company_employee_id}
                    onClick={() => navigate(`/petty-cash/${emp.company_employee_id}`)}
                    className="bg-white rounded-2xl border border-gray-150 p-5 shadow-sm active:scale-[0.99] transition-all flex flex-col space-y-4 cursor-pointer"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 min-w-0">
                        <span className="font-bold text-gray-800 truncate block">{emp.employee_name}</span>
                        <span className="text-xs text-gray-500 truncate block">{emp.designation || "No Designation"}</span>
                      </div>
                      <ChevronRightIcon className="w-6 h-6 text-gray-400 flex-shrink-0" />
                    </div>

                    <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100 text-xs">
                      <div>
                        <span className="text-gray-400 block mb-0.5 whitespace-nowrap">Total Income</span>
                        <span className="font-bold text-green-600 text-sm">&#8377;{emp.total_income}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block mb-0.5 whitespace-nowrap">Total Expenses</span>
                        <span className="font-bold text-red-600 text-sm">&#8377;{emp.total_expenses}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block mb-0.5 whitespace-nowrap">Pending</span>
                        <span className="font-bold text-amber-600 text-sm">&#8377;{emp.total_pending_expenses}</span>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-200 flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700">Auto-Approve</span>
                      <StatusCell
                        id={emp.company_employee_id}
                        idKey="company_employee_id"
                        initialStatus={emp.auto_approval_petty_cash || false}
                        url="petty-cash/update_auto_approval_status"
                        refreshUrl={["petty-cash/employee_list", "petty-cash/statistics"]}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            <RoundedContainer className="border border-gray-155 shadow-sm p-4">
              <Pagination
                currentPage={empFilters.page_number}
                lengthofItems={employeeListing.data?.total_count || 0}
                limit={empFilters.limit}
                onPageChange={handlePageClick}
              />
            </RoundedContainer>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminPettyCashList;
